import { useState, useEffect, useCallback, useRef } from 'react';
import { autoTrimImage } from '../../utils/imageTrim';

export const useCarCustomizer = (selectedCarModel = null, carModelData = null) => {
  const [selectedColor, setSelectedColor] = useState('#FFFFFF');
  const [selectedView, setSelectedView] = useState('Sida Vänster');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [placedGraphics, setPlacedGraphics] = useState({
    'Sida Vänster': [],
    'Sida Höger': [],
    'Fram': [],
    'Bak': [],
    'Topp': []
  });
  const [price, setPrice] = useState(0);
  const [helfoliering, setHelfoliering] = useState(false);
  const [carDimensions] = useState({ width: 873, height: 451 });
  const [vectorizedFiles, setVectorizedFiles] = useState(new Set());

  const HELFOLIERING_FEE = 10000;

  // Undo/Redo functionality
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoAction = useRef(false);
  const isInitialized = useRef(false);
  const MAX_HISTORY = 50; // Limit history to prevent memory issues

  // Calculate price directly from placedGraphics parameter to avoid stale closures
  const calculatePrice = useCallback((currentPlacedGraphics) => {
    const basePrice = 1500; // Uppdaterad grundpris
    
    // Get all placed graphics across all views
    const allPlacedGraphics = Object.values(currentPlacedGraphics).flat();
    
    // If no graphics are placed, price is 0
    if (allPlacedGraphics.length === 0) {
      return 0;
    }
    
    // Get unique raster files that need vectorization
    const uniqueRasterFiles = new Set(
      allPlacedGraphics
        .filter(g => {
          // Check if it's an image and has a raster extension
          return g.type === 'image' && 
                 g.name && // Make sure name exists
                 g.name.toLowerCase().match(/\.(png|jpg|jpeg)$/);
        })
        .map(g => g.originalFileId)
    );
    
    // Calculate vectorization costs (1500kr per unique raster file)
    const vectorizationCosts = uniqueRasterFiles.size * 1500;

    // Calculate area-based costs
    const calculateGraphicCost = (graphic) => {
      if (graphic.type === 'text') {
        // Set default dimensions for text if not yet set
        const width = graphic.width || 100; // Default width
        const height = graphic.height || 30; // Default height
        return (width * height / 10000) * 250; // Uppdaterad: 250kr per 10 000 px²
      }
      const area = graphic.width * graphic.height;
      return (area / 10000) * 250; // Uppdaterad: 250kr per 10 000 px²
    };

    // Total graphics cost
    const graphicsCost = allPlacedGraphics.reduce((sum, g) => sum + calculateGraphicCost(g), 0);

    // Calculate total - only include basePrice if graphics are placed
    return basePrice + vectorizationCosts + graphicsCost;
  }, []);

  // Update price whenever placedGraphics or helfoliering changes
  useEffect(() => {
    const base = calculatePrice(placedGraphics);
    setPrice(base + (helfoliering ? HELFOLIERING_FEE : 0));
  }, [placedGraphics, helfoliering, calculatePrice]);

  useEffect(() => {
    console.log('placedGraphics updated in hook:', placedGraphics);
  }, [placedGraphics]);

  const updateGraphicPosition = useCallback((id, x, y) => {
    console.log('Updating graphic position:', { id, x, y });
    setPlacedGraphics(prev => {
      const viewGraphics = prev[selectedView] || [];
      const updated = {
        ...prev,
        [selectedView]: viewGraphics.map(item =>
          item.id === id ? { ...item, x, y } : item
        )
      };
      console.log('Updated placedGraphics after position update:', updated);
      return updated;
    });
  }, [selectedView]);
  
  const updateGraphicSize = useCallback((id, width, height, realDimensions = null) => {
    console.log('Updating graphic size:', { id, width, height, realDimensions });
    setPlacedGraphics(prev => {
      const viewGraphics = prev[selectedView] || [];
      const updated = {
        ...prev,
        [selectedView]: viewGraphics.map(item => {
          if (item.id === id) {
            const updatedItem = { 
              ...item, 
              width, 
              height 
            };
            
            // Add real dimensions if provided
            if (realDimensions) {
              updatedItem.realWidth = realDimensions.width;
              updatedItem.realHeight = realDimensions.height;
              updatedItem.realUnit = realDimensions.unit || 'mm';
            }
            
            return updatedItem;
          }
          return item;
        })
      };
      console.log('Updated placedGraphics after size update:', updated);
      return updated;
    });
  }, [selectedView]);

  const updateGraphicRotation = useCallback((id, rotation) => {
    console.log('Updating graphic rotation:', { id, rotation });
    setPlacedGraphics(prev => {
      const viewGraphics = prev[selectedView] || [];
      const updated = {
        ...prev,
        [selectedView]: viewGraphics.map(item =>
          item.id === id ? { ...item, rotation } : item
        )
      };
      console.log('Updated placedGraphics after rotation update:', updated);
      return updated;
    });
  }, [selectedView]);

  // Helper function to calculate real dimensions (same logic as CarPreview)
  const calculateInitialRealDimensions = useCallback((canvasWidth, canvasHeight) => {
    if (!selectedCarModel || !carModelData) return { width: 0, height: 0 };

    // Get the real dimensions for the current view
    const viewDimensions = carModelData.dimensions[selectedView];
    if (!viewDimensions) return { width: 0, height: 0 };

    // We need to account for the car's scale in the view
    const carTransform = carModelData.transforms[selectedView];
    const carScale = carTransform.scale || 1;

    // Calculate the scale factors between canvas and real dimensions
    const scaleX = viewDimensions.realWidth / (carDimensions.width / carScale);
    const scaleY = viewDimensions.realHeight / (carDimensions.height / carScale);

    // Calculate real dimensions in millimeters
    const realWidth = Math.round(canvasWidth * scaleX);
    const realHeight = Math.round(canvasHeight * scaleY);

    return {
      width: realWidth,
      height: realHeight,
      unit: 'mm'
    };
  }, [selectedView, selectedCarModel, carModelData, carDimensions]);

  const placeGraphic = useCallback((file) => {
    const initialSize = Math.min(carDimensions.width, carDimensions.height) * 0.2;
    
    // Create a temporary image to get dimensions
    const img = new Image();
    img.src = file.src || file.url; // Ensure src or url is used
    
    // Wait for image to load to get its dimensions
    img.onload = async () => {
      if (!img) return; // Ensure img is not null

      // Auto-trim the image to remove white/transparent edges
      const trimResult = await autoTrimImage(img.src);
      
      console.log('Auto-trim result:', {
        originalSize: `${trimResult.originalWidth}x${trimResult.originalHeight}`,
        trimmedSize: `${trimResult.trimmedWidth}x${trimResult.trimmedHeight}`,
        offsetX: trimResult.offsetX,
        offsetY: trimResult.offsetY
      });

      // Calculate aspect ratio from trimmed dimensions
      const aspectRatio = trimResult.trimmedWidth / trimResult.trimmedHeight;
      
      // Calculate dimensions that maintain aspect ratio
      let width, height;
      if (aspectRatio > 1) {
        // Landscape
        width = initialSize;
        height = initialSize / aspectRatio;
      } else {
        // Portrait or square
        height = initialSize;
        width = initialSize * aspectRatio;
      }

      // Calculate initial real dimensions
      const realDimensions = calculateInitialRealDimensions(width, height);

      // Use functional update to get fresh state when image loads
      setPlacedGraphics(prev => {
        // Get highest z-index in current view from FRESH state
        const currentGraphics = prev[selectedView] || [];
        const highestZIndex = currentGraphics.reduce((max, item) => 
          Math.max(max, item.zIndex || 0), 0);

      const newGraphic = {
        id: Date.now(),
        type: 'image',
        src: trimResult.src, // Use trimmed image
        name: file.name,
        x: (carDimensions.width - width) / 2,
        y: (carDimensions.height - height) / 2,
        width,
        height,
        realWidth: realDimensions.width,
        realHeight: realDimensions.height,
        rotation: 0,
        originalFileId: file.id,
        zIndex: highestZIndex + 1
      };

        return {
        ...prev,
        [selectedView]: [...(prev[selectedView] || []), newGraphic]
        };
      });
    };

    // Start loading the image
    img.src = file.src || file.url; // Ensure src or url is used
  }, [selectedView, carDimensions, calculateInitialRealDimensions]);

  const placeText = useCallback((textConfig) => {
    console.log('Placing text with config:', textConfig);
    
    // Ensure text has default dimensions if not provided
    const newText = {
      ...textConfig,
      id: Date.now(),
      type: 'text',
      x: 436.5, // Default position
      y: 225.5,
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      fontSize: 40,
      // Set default dimensions if not provided to ensure price calculation works
      width: textConfig.width || 100,
      height: textConfig.height || 30
    };

    setPlacedGraphics(prev => {
      const viewGraphics = prev[selectedView] || [];
      const updated = {
        ...prev,
        [selectedView]: [...viewGraphics, newText]
      };
      console.log('Updated placedGraphics after adding text:', updated);
      return updated;
    });
  }, [selectedView]);

  const placeShape = useCallback((shapeConfig) => {
    console.log('Placing shape with config:', shapeConfig);
    
    const newShape = {
      ...shapeConfig,
      id: shapeConfig.id || Date.now(),
      type: 'shape',
      rotation: shapeConfig.rotation || 0
    };

    setPlacedGraphics(prev => {
      const viewGraphics = prev[selectedView] || [];
      const updated = {
        ...prev,
        [selectedView]: [...viewGraphics, newShape]
      };
      console.log('Updated placedGraphics after adding shape:', updated);
      return updated;
    });
  }, [selectedView]);

  const deleteGraphic = (id) => {
    setPlacedGraphics(prev => ({
      ...prev,
      [selectedView]: prev[selectedView].filter(graphic => graphic.id !== id)
    }));
  };

  const handleFileUpload = async (file, needsVectorization = false) => {
    // Create a unique ID for the file
    const fileId = Math.random().toString(36).substr(2, 9);
    
    // Create a URL for the file
    const fileUrl = URL.createObjectURL(file);
    
    // Create the file object
    const uploadedFile = {
      id: fileId,
      name: file.name,
      src: fileUrl,
      needsVectorization
    };

    // Add to uploaded files
    setUploadedFiles(prev => [...prev, uploadedFile]);
    
    return uploadedFile; // Return the uploaded file object
  };

  const removeUploadedFile = (id) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== id));
    // Also remove any placed instances of this graphic
    setPlacedGraphics(prev => {
      const newPlacedGraphics = {...prev};
      Object.keys(newPlacedGraphics).forEach(view => {
        newPlacedGraphics[view] = newPlacedGraphics[view].filter(graphic => graphic.id !== id);
      });
      return newPlacedGraphics;
    });
  };

  const updateGraphicZIndex = (id, action) => {
    setPlacedGraphics(prev => {
      const viewGraphics = [...prev[selectedView]];
      const index = viewGraphics.findIndex(g => g.id === id);
      if (index === -1) return prev;

      const graphic = {...viewGraphics[index]};
      
      // Initialize z-indices if not set (assign based on current array order)
      viewGraphics.forEach((g, i) => {
        if (g.zIndex === undefined) {
          g.zIndex = i;
        }
      });
      
      // Get current z-indices
      const zIndices = viewGraphics.map(g => g.zIndex || 0);
      const currentZ = graphic.zIndex || 0;
      
      if (action === 'forward') {
        // Move up one layer (increase z-index)
        const higherGraphics = viewGraphics.filter(g => g.zIndex > currentZ);
        if (higherGraphics.length > 0) {
          // Find the graphic immediately above
          const nextGraphic = higherGraphics.reduce((closest, g) => 
            (!closest || g.zIndex < closest.zIndex) ? g : closest
          , null);
          
          if (nextGraphic) {
            // Swap z-indices
            const temp = graphic.zIndex;
            graphic.zIndex = nextGraphic.zIndex;
            nextGraphic.zIndex = temp;
          }
        }
      } else if (action === 'backward') {
        // Move down one layer (decrease z-index)
        const lowerGraphics = viewGraphics.filter(g => g.zIndex < currentZ);
        if (lowerGraphics.length > 0) {
          // Find the graphic immediately below
          const prevGraphic = lowerGraphics.reduce((closest, g) => 
            (!closest || g.zIndex > closest.zIndex) ? g : closest
          , null);
          
          if (prevGraphic) {
            // Swap z-indices
            const temp = graphic.zIndex;
            graphic.zIndex = prevGraphic.zIndex;
            prevGraphic.zIndex = temp;
          }
        }
      } else if (action === 'toFront') {
        // Move above the highest z-index
        const highestZ = Math.max(...zIndices);
        if (currentZ <= highestZ) {
          graphic.zIndex = highestZ + 1;
        }
      } else if (action === 'toBack') {
        // Move below the lowest z-index
        const lowestZ = Math.min(...zIndices);
        if (currentZ >= lowestZ) {
          graphic.zIndex = lowestZ - 1;
        }
      }

      viewGraphics[index] = graphic;
      
      // Sort graphics by z-index
      viewGraphics.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
      
      return {
        ...prev,
        [selectedView]: viewGraphics
      };
    });
  };

  const duplicateGraphic = (originalId, newId, targetView = selectedView) => {
    console.log('duplicateGraphic called with:', { originalId, newId, targetView });
    
    return new Promise((resolve) => {
      // Find the original graphic first
      const originalGraphic = placedGraphics[selectedView].find(g => g.id === originalId);
      console.log('Found original graphic:', originalGraphic);
      
      if (!originalGraphic) {
        console.log('No original graphic found');
        resolve(null);
        return;
      }

      // Create the duplicate with the new ID
      const duplicate = {
        ...originalGraphic,
        id: newId,
        view: targetView,
        type: originalGraphic.type,
        src: originalGraphic.src,
        name: originalGraphic.name,
        originalFileId: originalGraphic.originalFileId,
        rotation: originalGraphic.rotation || 0,
        width: originalGraphic.width,
        height: originalGraphic.height,
        realWidth: originalGraphic.realWidth,
        realHeight: originalGraphic.realHeight,
        zIndex: originalGraphic.zIndex,
        y: originalGraphic.y
      };

      // Calculate new X position for mirroring
      const centerX = carDimensions.width / 2;
      const distanceFromCenter = originalGraphic.x - centerX;
      duplicate.x = centerX - distanceFromCenter;
      
      // Handle flipping based on type
      if (originalGraphic.type === 'text') {
        duplicate.flipX = !originalGraphic.flipX;
      } else if (originalGraphic.type === 'image') {
        duplicate.scaleX = originalGraphic.scaleX ? -originalGraphic.scaleX : -1;
      }

      console.log('Created duplicate:', duplicate);

      // Create the new state first
      const newState = {
        ...placedGraphics,
        [targetView]: [...(placedGraphics[targetView] || []), duplicate]
      };

      // Update state and resolve with the new state
      setPlacedGraphics(newState);
      console.log('State updated with:', newState);
      
      // Price will update automatically via useEffect
      resolve(newState);
    });
  };


  // Track changes to placedGraphics for undo/redo
  useEffect(() => {
    // Skip if this is an undo/redo action
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }
    
    // Check if current state has any graphics
    const hasGraphics = Object.values(placedGraphics).some(view => view.length > 0);
    
    // Initialize history only when first graphic is placed (not on mount with empty state)
    if (!isInitialized.current) {
      if (hasGraphics) {
        isInitialized.current = true;
        setHistory([JSON.parse(JSON.stringify(placedGraphics))]);
        setHistoryIndex(0);
        console.log('History initialized with first graphic');
      }
      return;
    }
    
    // Add current state to history
    setHistory(prev => {
      // Remove any future history if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      
      // Add new state
      newHistory.push(JSON.parse(JSON.stringify(placedGraphics)));
      
      console.log('History saved. Total entries:', newHistory.length, 'Current index will be:', historyIndex + 1);

      // Limit history size
      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
        return newHistory;
      }
      
      return newHistory;
    });
    
    // Update index
    setHistoryIndex(prev => {
      const newIndex = prev + 1;
      return newIndex >= MAX_HISTORY ? MAX_HISTORY - 1 : newIndex;
    });
  }, [placedGraphics]);
  
  // Undo function
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const previousState = history[historyIndex - 1];
      console.log('Undo: Going from index', historyIndex, 'to', historyIndex - 1);
      console.log('Previous state:', previousState);
      isUndoRedoAction.current = true;
      setPlacedGraphics(JSON.parse(JSON.stringify(previousState)));
      setHistoryIndex(historyIndex - 1);
    } else {
      console.log('Cannot undo: At beginning of history');
    }
  }, [historyIndex, history]);
  
  // Redo function
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      console.log('Redo: Going from index', historyIndex, 'to', historyIndex + 1);
      isUndoRedoAction.current = true;
      const nextState = history[historyIndex + 1];
      setPlacedGraphics(JSON.parse(JSON.stringify(nextState)));
      setHistoryIndex(historyIndex + 1);
    } else {
      console.log('Cannot redo: At end of history');
    }
  }, [historyIndex, history]);
  
  // Check if undo/redo is available
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  return {
    selectedColor,
    setSelectedColor,
    helfoliering,
    setHelfoliering,
    HELFOLIERING_FEE,
    selectedView,
    setSelectedView,
    uploadedFiles,
    setUploadedFiles,
    placedGraphics,
    setPlacedGraphics,
    price,
    carDimensions,
    handleFileUpload,
    placeGraphic,
    updateGraphicPosition,
    updateGraphicSize,
    updateGraphicRotation,
    deleteGraphic,
    updateGraphicZIndex,
    removeUploadedFile,
    placeText,
    placeShape,
    undo,
    redo,
    canUndo,
    canRedo,
    duplicateGraphic,
    selectedCarModel
  };
};